// FILE: src/pages/admin/staff/AdminStaffListPage.jsx
// Staff (RBAC; needs staff.manage): every staff account with its role, its
// Digital Driving Instructor registration (needed to answer course-content
// questions) and its status (invitation sent / active / locked) as icon +
// words. "Invite staff member" adds one; Edit changes the role, the DDI
// number and the contact details. Students are on the Students page.
// GET /api/admin/users?role=admin → { users: [… staffRole { id, name }], totalCount, … }
import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Alert, Box, Button, Link, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from "../../../components/ui";
import { ResponsiveTable } from "../../../components/admin/AdminBits";
import { ServerPagination } from "../../../components/admin/AuditLogTable";
import { PhoneCard, PhoneList, useIsPhone } from "../../../components/admin/PhoneList";
import { personName, formatNumber } from "../../../components/admin/format";
import { DdiText, StaffRolesNav, StaffStatusChip } from "../../../components/admin/staffBits";
import { listStaff } from "../../../services/roles";

const LIMIT = 50;

function roleText(user) {
  return user.staffRole?.name || "No role";
}

export default function AdminStaffListPage() {
  const location = useLocation();
  const [notice, setNotice] = useState(location.state?.notice || "");
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ users: null, totalCount: 0, error: null, loading: true });
  const phone = useIsPhone();

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: null }));
    listStaff({ page, limit: LIMIT })
      .then((data) => setState({ users: Array.isArray(data?.users) ? data.users : [], totalCount: Number(data?.totalCount) || 0, error: null, loading: false }))
      .catch((error) => setState((s) => ({ ...s, error: error || "INTERNAL_ERROR", loading: false })));
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const invite = (props = {}) => (
    <Button component={RouterLink} to="/admin/staff/invite" variant="contained" startIcon={<PersonAddAlt1OutlinedIcon />} {...props}>
      Invite staff member
    </Button>
  );
  const edit = (user, name) => (
    <Button component={RouterLink} to={`/admin/users/${user._id}/edit`} variant="outlined" startIcon={<EditOutlinedIcon />} aria-label={`Edit ${name}`}>
      Edit
    </Button>
  );
  const { users, totalCount, error, loading } = state;

  return (
    <Box>
      <PageHeader
        title="Staff"
        description="Everyone who works in the admin area, with the role that decides what they can do. Course-content questions can only be answered by staff with a DDI registration number."
        actions={invite()}
      />
      <StaffRolesNav current="/admin/staff" />

      {notice && (
        <Alert severity="success" role="status" sx={{ mb: 2.5 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      <Section
        id="staff-list"
        title="Staff accounts"
        description={users ? `${formatNumber(totalCount)} ${totalCount === 1 ? "account" : "accounts"}.` : undefined}
        contentSx={{ p: { xs: 2, sm: 3 } }}
      >
        {users === null && error ? (
          <ErrorState error={error} title="We couldn't load the staff list" onRetry={load} />
        ) : users === null ? (
          <LoadingState variant="table" rows={4} label="Loading staff…" />
        ) : users.length === 0 ? (
          <EmptyState icon={BadgeOutlinedIcon} title="No staff accounts yet" description="Invite a staff member and choose their role." action={invite()} />
        ) : (
          <>
            {error && <ErrorState compact error={error} onRetry={load} />}
            {phone ? (
              <PhoneList label="Staff">
                {users.map((user) => {
                  const name = personName(user, "Name not given");
                  return (
                    <PhoneCard key={user._id}>
                      <Box sx={{ minWidth: 0 }}>
                        <Link component={RouterLink} to={`/admin/users/${user._id}`} sx={{ fontWeight: 650 }}>
                          {name}
                        </Link>
                        <Typography sx={{ color: "text.secondary", overflowWrap: "anywhere" }}>{user.email}</Typography>
                      </Box>
                      <Typography>
                        <Box component="span" sx={{ color: "text.secondary" }}>
                          Role:{" "}
                        </Box>
                        {roleText(user)}
                      </Typography>
                      <Box>
                        <Typography component="span" sx={{ color: "text.secondary" }}>
                          DDI registration:
                        </Typography>
                        <DdiText user={user} />
                      </Box>
                      <Box>
                        <StaffStatusChip user={user} />
                      </Box>
                      <Box>{edit(user, name)}</Box>
                    </PhoneCard>
                  );
                })}
              </PhoneList>
            ) : (
              <ResponsiveTable label="Staff" minWidth={820}>
                <TableHead>
                  <TableRow>
                    <TableCell component="th" scope="col">
                      Name
                    </TableCell>
                    <TableCell component="th" scope="col">
                      Role
                    </TableCell>
                    <TableCell component="th" scope="col">
                      DDI registration
                    </TableCell>
                    <TableCell component="th" scope="col">
                      Status
                    </TableCell>
                    <TableCell component="th" scope="col" align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => {
                    const name = personName(user, "Name not given");
                    return (
                      <TableRow key={user._id} hover>
                        <TableCell component="th" scope="row" sx={{ maxWidth: 300 }}>
                          <Link component={RouterLink} to={`/admin/users/${user._id}`} sx={{ fontWeight: 600 }}>
                            {name}
                          </Link>
                          <Typography sx={{ color: "text.secondary", overflowWrap: "anywhere" }}>{user.email}</Typography>
                        </TableCell>
                        <TableCell>{roleText(user)}</TableCell>
                        <TableCell>
                          <DdiText user={user} />
                        </TableCell>
                        <TableCell>
                          <StaffStatusChip user={user} />
                        </TableCell>
                        <TableCell align="right">{edit(user, name)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </ResponsiveTable>
            )}
            {totalCount > LIMIT && (
              <ServerPagination totalCount={totalCount} page={page} limit={LIMIT} options={[LIMIT]} onPage={setPage} onLimit={() => {}} />
            )}
            {loading && <Typography role="status" sx={{ mt: 1, color: "text.secondary" }}>Updating…</Typography>}
          </>
        )}
      </Section>
    </Box>
  );
}
